/**
 * useAboutAnalytics.js
 *
 * Centralised analytics hook for the About section.
 * Mirrors the exact pattern of useBlogAnalytics.js / usePackagesAnalytics.js:
 *   - IS_PRODUCTION guard excludes localhost + 127.0.0.1
 *   - Dev mode: console.group + console.table with [AboutAnalytics] prefix
 *   - react-ga4 used in production
 *
 * HOW TO USE:
 *   const analytics = useAboutAnalytics();
 *   analytics.trackBookTrialClick();
 *
 * DEV MODE:
 *   Open browser DevTools → Console to see all events grouped under
 *   [AboutAnalytics] in real time.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[AboutAnalytics] ${eventName}`, "color:#3d6b4f;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useAboutAnalytics = () => {

  // ── Page / section view ───────────────────────────────────────────────────────

  const trackSectionView = useCallback((url) => {
    send("about_section_view", {
      page_title:    "About — Yoga Shastra",
      page_location: url,
    });
  }, []);

  // ── CTA ───────────────────────────────────────────────────────────────────────

  const trackBookTrialClick = useCallback(() => {
    send("about_book_trial_click", {
      cta_label:   "Book a Trial",
      destination: "contactUs",
    });
  }, []);

  return {
    trackSectionView,
    trackBookTrialClick,
  };
};

export default useAboutAnalytics;
